import React, { useState, useCallback, useRef, useEffect } from "react";
import { useLoader } from "../../contexts/LoaderContext";
import { showToast } from "../Toaster";
import TableComponent from "../TableComponent/TableComponent";
import StatsBoxContainer from "../StatsBoxContainer/StatsBoxContainer";
import Modal from "../Modal/Modal";
import * as XLSX from "xlsx";
import JobHistoryService from "../../services/JobHistoryService";
import { WorkerMessage } from "../../workers/types";
import "./JobHistoryUploadComponent.scss";

// Expected headers for job history upload
const EXPECTED_HEADERS = [
  "JOB_ID",
  "Gate Open",
  "Gate Close",
  "Transporter Name",
  "Booking From Destination",
  "To Destination",
  "CONTAINER NO",
  "SIZE",
  "TYPE",
  "TRUCK NO",
  "Driver Name",
  "Contact Number",
  "Source Lat,Lng",
  "Dest Lat,Lng",
  "Cycle",
  "Job Type",
];

interface UploadState {
  id: string;
  filename: string;
  status: "UPLOADING" | "PROCESSING" | "FAILED" | "UPLOADED" | "READY";
  progress: number;
  validationResults?: {
    total: number;
    valid: number;
    invalid: number;
    errors: Array<{
      row: number;
      message: string;
    }>;
    headers?: string[];
    data?: Record<string, string>[];
  };
  responseMessage?: string;
}

interface PreviewData {
  headers: string[];
  rows: Record<string, string>[];
  filename: string;
  isEditMode?: boolean;
  status?: string;
}

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: PreviewData | null;
  isEditMode: boolean;
  onSubmit: (data: PreviewData) => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  onClose,
  data,
  isEditMode,
  onSubmit,
}) => {
  const [editableData, setEditableData] = useState<PreviewData | null>(data);

  const handleCellChange = useCallback(
    (rowIndex: number, header: string, value: string) => {
      if (!editableData) return;

      const newRows = [...editableData.rows];
      newRows[rowIndex] = {
        ...newRows[rowIndex],
        [header]: value,
      };

      setEditableData({
        ...editableData,
        rows: newRows,
      });
    },
    [editableData]
  );

  const handleSubmit = useCallback(() => {
    if (editableData && onSubmit) {
      onSubmit(editableData);
    }
  }, [editableData, onSubmit]);

  const formatHeaderLabel = useCallback((header: string) => {
    return header
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }, []);

  const hasDuplicateJobIds =
    data?.rows &&
    data.rows.length > 0 &&
    data.headers.includes("JOB_ID") &&
    new Set(data.rows.map((row) => row["JOB_ID"])).size !== data.rows.length;

  const hasError = editableData?.headers[0] === "Error";

  const footer = (
    <div className="flex justify-end gap-4">
      <button className="secondary_button" onClick={onClose}>
        {isEditMode ? "Cancel" : "Close"}
      </button>
      {isEditMode && !hasError && (
        <button className="primary_button" onClick={handleSubmit}>
          Upload
        </button>
      )}
    </div>
  );

  const getStatusMessage = () => {
    if (data?.status === "PROCESSING") {
      return "The file is still being processed. Please wait...";
    } else if (data?.status === "FAILED") {
      return "The file could not be processed due to an error.";
    } else if (data?.status === "UPLOADING") {
      return "The file is currently being uploaded...";
    }
    return "The file does not contain any valid data.";
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={data?.filename || "Preview"}
      footer={footer}
      className="max-w-[90vw] h-[90vh]"
      size="full"
    >
      <div className="flex flex-col h-full">
        {hasError && (
          <div className="p-4 bg-red-50 rounded-lg mb-4">
            <p className="text-red-600 font-medium">
              {editableData?.rows[0]?.error ||
                "An error occurred while processing the file"}
            </p>
          </div>
        )}
        {!hasError && (
          <div className="mb-4">
            <p className="text-gray-600">
              {isEditMode
                ? "Review and edit the data before uploading"
                : editableData?.rows && editableData.rows.length > 0
                ? "This is a read-only view of the uploaded file"
                : ""}
            </p>
            {hasDuplicateJobIds && (
              <p className="text-red-500 mt-2">
                Warning: Duplicate Job IDs detected. Please ensure each Job ID
                is unique.
              </p>
            )}
          </div>
        )}
        {editableData?.rows && editableData.rows.length > 0 ? (
          <div className="flex-1 min-h-0 overflow-hidden">
            <div className="table-wrapper">
              <table className="preview-table">
                <thead>
                  <tr>
                    {editableData.headers.map((header: string) => (
                      <th key={header} className="header-cell">
                        {formatHeaderLabel(header)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {editableData.rows.map((row: any, rowIndex: number) => (
                    <tr key={rowIndex}>
                      {editableData.headers.map((header: string) => (
                        <td key={`${rowIndex}-${header}`} className="data-cell">
                          {isEditMode ? (
                            <input
                              type="text"
                              className="cell-input"
                              value={row[header] || ""}
                              onChange={(e) =>
                                handleCellChange(
                                  rowIndex,
                                  header,
                                  e.target.value
                                )
                              }
                            />
                          ) : (
                            <span className="cell-text">{row[header]}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500 h-full">
            <div className="text-center">
              <p className="text-lg font-medium mb-2">No data available</p>
              <p className="text-sm">{getStatusMessage()}</p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

const JobHistoryUploadComponent: React.FC = () => {
  const [uploads, setUploads] = useState<UploadState[]>([]);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [editableData, setEditableData] = useState<Record<string, string>[]>(
    []
  );
  const [processingProgress, setProcessingProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [progressText, setProgressText] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [processedRows, setProcessedRows] = useState(0);
  const { showLoader, hideLoader } = useLoader();

  const workerRef = useRef<Worker | null>(null);
  const currentUploadIdRef = useRef<string | null>(null);
  const processedDataRef = useRef<Record<string, string>[]>([]);
  const headersRef = useRef<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadsRef = useRef<UploadState[]>([]);

  // Keep uploadsRef in sync with uploads state
  useEffect(() => {
    uploadsRef.current = uploads;
  }, [uploads]);

  const validateHeaders = useCallback(
    (
      headers: string[]
    ): {
      isValid: boolean;
      missingHeaders: string[];
      extraHeaders: string[];
    } => {
      if (!headers || !Array.isArray(headers)) {
        return {
          isValid: false,
          missingHeaders: EXPECTED_HEADERS,
          extraHeaders: [],
        };
      }

      const normalizedHeaders = headers.map((header) =>
        String(header).trim().toUpperCase()
      );

      const missingHeaders = EXPECTED_HEADERS.filter(
        (expectedHeader) =>
          !normalizedHeaders.includes(expectedHeader.toUpperCase())
      );

      const extraHeaders = normalizedHeaders.filter(
        (header) =>
          !EXPECTED_HEADERS.some(
            (expectedHeader) => expectedHeader.toUpperCase() === header
          )
      );

      return {
        isValid: missingHeaders.length === 0,
        missingHeaders,
        extraHeaders,
      };
    },
    []
  );

  const handleWorkerMessage = useCallback(
    (event: MessageEvent<WorkerMessage>) => {
      const {
        headers,
        chunks,
        chunk,
        chunkIndex,
        totalChunks,
        totalRows: workerTotalRows,
        error,
        done,
      } = event.data;

      if (error) {
        showToast(`Error processing file: ${error}`, "error");

        if (currentUploadIdRef.current) {
          setUploads((prev) =>
            prev.map((upload) => {
              if (upload.id === currentUploadIdRef.current) {
                return {
                  ...upload,
                  status: "FAILED",
                  progress: 100,
                  validationResults: {
                    total: 0,
                    valid: 0,
                    invalid: 0,
                    errors: [{ row: 0, message: error }],
                  },
                };
              }
              return upload;
            })
          );
        }

        hideLoader();
        setShowProgress(false);
        return;
      }

      if (headers && Array.isArray(headers)) {
        headersRef.current = headers;
      }

      if (done) {
        if (currentUploadIdRef.current) {
          // Validate headers before proceeding
          const validationResult = validateHeaders(headersRef.current);
          if (!validationResult.isValid) {
            setUploads((prev) =>
              prev.map((upload) => {
                if (upload.id === currentUploadIdRef.current) {
                  return {
                    ...upload,
                    status: "FAILED",
                    progress: 100,
                    validationResults: {
                      total: processedDataRef.current.length,
                      valid: 0,
                      invalid: processedDataRef.current.length,
                      errors: [
                        {
                          row: 0,
                          message: `File does not match the required template format. Missing headers: ${validationResult.missingHeaders.join(
                            ", "
                          )}`,
                        },
                      ],
                    },
                    responseMessage: "Invalid format",
                  };
                }
                return upload;
              })
            );
            hideLoader();
            setShowProgress(false);
            return;
          }

          // If validation passes, automatically start upload
          const previewData: PreviewData = {
            headers: headersRef.current,
            rows: processedDataRef.current,
            filename:
              uploadsRef.current.find(
                (u) => u.id === currentUploadIdRef.current
              )?.filename || "",
            isEditMode: true,
            status: "READY",
          };
          handlePreviewSubmit(previewData);
        }

        setTotalRows(0);
        setProcessedRows(0);
        setProcessingProgress(0);
        setProgressText("");
        setShowProgress(false);
        hideLoader();
        return;
      }

      if (chunks) {
        setShowProgress(true);
        const totalRowsCount = workerTotalRows || 0;
        setTotalRows(totalRowsCount);
        setProcessedRows(0);
        setProgressText("Processing file...");

        const firstChunk = chunks[0];
        if (Array.isArray(firstChunk)) {
          const processedRows = firstChunk.map((row: any[]) => {
            const rowData: Record<string, string> = {};
            headersRef.current.forEach((header: string, index: number) => {
              const value = row[index];
              rowData[header] =
                value !== undefined && value !== null
                  ? String(value).trim()
                  : "";
            });
            return rowData;
          });

          processedDataRef.current = processedRows;
          setEditableData(processedRows);
          setProcessedRows(processedRows.length);

          const progress = Math.min(
            100,
            Math.round((processedRows.length / totalRowsCount) * 100)
          );
          setProcessingProgress(progress);
          setProgressText(`Processed ${processedRows.length} rows`);

          if (currentUploadIdRef.current) {
            setUploads((prev) =>
              prev.map((upload) => {
                if (upload.id === currentUploadIdRef.current) {
                  return {
                    ...upload,
                    progress: progress,
                    status: "PROCESSING",
                  };
                }
                return upload;
              })
            );
          }
        }
      } else if (chunk) {
        if (Array.isArray(chunk)) {
          const processedRows = chunk.map((row: any[]) => {
            const rowData: Record<string, string> = {};
            headersRef.current.forEach((header: string, index: number) => {
              const value = row[index];
              rowData[header] =
                value !== undefined && value !== null
                  ? String(value).trim()
                  : "";
            });
            return rowData;
          });

          processedDataRef.current = [
            ...processedDataRef.current,
            ...processedRows,
          ];
          setEditableData(processedDataRef.current);
          const newProcessedRows = processedDataRef.current.length;
          setProcessedRows(newProcessedRows);

          const progress = Math.min(
            100,
            Math.round((newProcessedRows / totalRows) * 100)
          );
          setProcessingProgress(progress);
          setProgressText(`Processed ${newProcessedRows} rows`);

          if (currentUploadIdRef.current) {
            setUploads((prev) =>
              prev.map((upload) => {
                if (upload.id === currentUploadIdRef.current) {
                  return {
                    ...upload,
                    progress: progress,
                    status: "PROCESSING",
                  };
                }
                return upload;
              })
            );
          }
        }
      }
    },
    [hideLoader, validateHeaders]
  );

  useEffect(() => {
    workerRef.current = new Worker(
      new URL("../../workers/excelWorker.ts", import.meta.url)
    );
    workerRef.current.onmessage = handleWorkerMessage;

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [handleWorkerMessage]);

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) {
        showToast("No file selected", "error");
        return;
      }

      const file = files[0];

      try {
        if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
          showToast(`File "${file.name}" is not an Excel file`, "error");
          return;
        }

        const existingUpload = uploads.find(
          (upload) => upload.filename === file.name
        );

        if (existingUpload) {
          showToast(`File "${file.name}" has already been uploaded`, "warning");
          return;
        }

        const uploadId = Date.now().toString();
        currentUploadIdRef.current = uploadId;

        const newUpload: UploadState = {
          id: uploadId,
          filename: file.name,
          status: "PROCESSING",
          progress: 0,
        };
        setUploads((prev) => [...prev, newUpload]);

        if (workerRef.current) {
          workerRef.current.postMessage({ file });
        }
      } catch (error) {
        showToast("Failed to process file", "error");
        if (currentUploadIdRef.current) {
          setUploads((prev) =>
            prev.map((upload) => {
              if (upload.id === currentUploadIdRef.current) {
                return {
                  ...upload,
                  status: "FAILED",
                  progress: 100,
                  validationResults: {
                    total: 0,
                    valid: 0,
                    invalid: 0,
                    errors: [{ row: 0, message: "Failed to process file" }],
                  },
                };
              }
              return upload;
            })
          );
        }
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [uploads]
  );

  const handleDownloadTemplate = useCallback(() => {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([EXPECTED_HEADERS]);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

    const date = new Date();
    const formattedDate = date.toLocaleDateString("en-GB").replace(/\//g, "_");
    const filename = `st_jobs_upload_${formattedDate}.xlsx`;

    XLSX.writeFile(workbook, filename);
  }, []);

  const handleUploadClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handlePreviewClick = useCallback((row: UploadState) => {
    currentUploadIdRef.current = row.id;

    const previewData: PreviewData = {
      headers: row.validationResults?.headers || EXPECTED_HEADERS,
      rows: row.validationResults?.data || [],
      filename: row.filename,
      isEditMode: row.status === "READY",
      status: row.status,
    };

    setPreviewData(previewData);
    setEditableData(row.validationResults?.data || []);
    setIsPreviewModalOpen(true);
  }, []);

  const handlePreviewSubmit = useCallback(async (data: PreviewData) => {
    try {
      const currentUpload = uploadsRef.current.find(
        (upload) => upload.id === currentUploadIdRef.current
      );

      if (!currentUpload) {
        showToast("File not found. Please try uploading again.", "error");
        return;
      }

      const totalRecords = data.rows.length;

      setUploads((prev) =>
        prev.map((upload) => {
          if (upload.id === currentUploadIdRef.current) {
            return {
              ...upload,
              status: "UPLOADING",
              progress: 0,
              validationResults: {
                total: totalRecords,
                valid: totalRecords,
                invalid: 0,
                errors: [],
                headers: data.headers,
                data: data.rows,
              },
              responseMessage: "Uploading...",
            };
          }
          return upload;
        })
      );

      const worksheet = XLSX.utils.json_to_sheet(data.rows, {
        header: data.headers,
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const date = new Date();
      const formattedDate = date
        .toLocaleDateString("en-GB")
        .replace(/\//g, "_");
      const filename = `st_job_history_${formattedDate}.xlsx`;

      const file = new File([excelBuffer], filename, {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      setUploads((prev) =>
        prev.map((upload) => {
          if (upload.id === currentUploadIdRef.current) {
            return {
              ...upload,
              status: "UPLOADING",
              progress: 50,
              validationResults: {
                total: totalRecords,
                valid: totalRecords,
                invalid: 0,
                errors: [],
                headers: data.headers,
                data: data.rows,
              },
              responseMessage: "Uploading...",
            };
          }
          return upload;
        })
      );

      const response = await JobHistoryService.uploadExcelFile(file);

      if (response.data.error) {
        setUploads((prev) =>
          prev.map((upload) => {
            if (upload.id === currentUploadIdRef.current) {
              return {
                ...upload,
                status: "FAILED",
                progress: 100,
                validationResults: {
                  total: totalRecords,
                  valid: 0,
                  invalid: totalRecords,
                  errors: [{ row: 0, message: response.data.message }],
                  headers: data.headers,
                  data: data.rows,
                },
                responseMessage: response.data.message,
              };
            }
            return upload;
          })
        );
      } else {
        const uploadedData = {
          headers: data.headers,
          data: data.rows,
          total: totalRecords,
          valid: totalRecords,
          invalid: 0,
          errors: [],
        };

        setUploads((prev) =>
          prev.map((upload) => {
            if (upload.id === currentUploadIdRef.current) {
              return {
                ...upload,
                status: "UPLOADED",
                progress: 100,
                validationResults: uploadedData,
                responseMessage: "File uploaded successfully",
              };
            }
            return upload;
          })
        );
        showToast("File uploaded successfully", "success");
      }
    } catch (error) {
      showToast("Failed to upload file", "error");

      if (currentUploadIdRef.current) {
        setUploads((prev) =>
          prev.map((upload) => {
            if (upload.id === currentUploadIdRef.current) {
              return {
                ...upload,
                status: "FAILED",
                progress: 100,
                validationResults: {
                  total: data.rows.length,
                  valid: 0,
                  invalid: data.rows.length,
                  errors: [{ row: 0, message: "Failed to upload file" }],
                  headers: data.headers,
                  data: data.rows,
                },
                responseMessage: "Failed to upload file",
              };
            }
            return upload;
          })
        );
      }
    } finally {
      setIsPreviewModalOpen(false);
      setPreviewData(null);
      setEditableData([]);
      processedDataRef.current = [];
    }
  }, []);

  const handleDelete = useCallback((uploadId: string) => {
    setUploads((prev) => prev.filter((upload) => upload.id !== uploadId));
  }, []);

  const handlePreviewClose = useCallback(() => {
    setIsPreviewModalOpen(false);
    setPreviewData(null);
    setEditableData([]);
    setShowProgress(false);
    setProgressText("");
    setTotalRows(0);
    setProcessedRows(0);
    setProcessingProgress(0);

    if (currentUploadIdRef.current) {
      setUploads((prev) =>
        prev.map((upload) => {
          if (upload.id === currentUploadIdRef.current) {
            if (!validateHeaders(headersRef.current).isValid) {
              return {
                ...upload,
                status: "FAILED",
                progress: 100,
                validationResults: {
                  total: 0,
                  valid: 0,
                  invalid: 0,
                  errors: [
                    {
                      row: 0,
                      message:
                        "File does not match the required template format. Please download and use the template.",
                    },
                  ],
                },
              };
            }
            return {
              ...upload,
              status: "UPLOADED",
              progress: 100,
              validationResults: {
                total: processedDataRef.current.length,
                valid: processedDataRef.current.length,
                invalid: 0,
                errors: [],
              },
            };
          }
          return upload;
        })
      );
      currentUploadIdRef.current = null;
    }

    processedDataRef.current = [];
    headersRef.current = [];
  }, [validateHeaders]);

  const statsData = [
    {
      name: "Total Uploads",
      value: uploads.length,
      valueColor: "#59585c",
    },
    {
      name: "Valid Uploads",
      value: uploads.filter((u) => u.status === "UPLOADED").length,
      valueColor: "#2e7d32",
    },
    {
      name: "Failed Uploads",
      value: uploads.filter((u) => u.status === "FAILED").length,
      valueColor: "#d32f2f",
    },
    {
      name: "Processing",
      value: uploads.filter((u) => u.status === "PROCESSING").length,
      valueColor: "#69b9bc",
    },
  ];

  const columns = [
    {
      header: "Filename",
      accessor: "filename",
      showTooltip: true,
      cell: (value: any, row: Record<string, any>) => (
        <span
          className={`${
            row.status === "FAILED"
              ? "text-gray-500"
              : "text-[#027CB5] hover:underline cursor-pointer"
          }`}
          onClick={() =>
            row.status !== "FAILED" && handlePreviewClick(row as UploadState)
          }
        >
          {value}
        </span>
      ),
    },
    {
      header: "Total Records",
      accessor: "validationResults.total",
      width: "150px",
      centerAlign: true,
      cell: (value: any, row: Record<string, any>) => {
        if (!row.validationResults) return "N/A";
        return row.validationResults.total.toLocaleString();
      },
    },
    {
      header: "Progress",
      accessor: "progress",
      cell: (value: any, row: Record<string, any>) => {
        if (row.status === "PROCESSING") {
          return (
            <div className="w-full">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">
                  {progressText || "Processing..."}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-blueText transition-all duration-300"
                  style={{ width: `${value}%` }}
                />
              </div>
            </div>
          );
        }
        return (
          <div className="w-full">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-600">{value}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  row.status === "FAILED"
                    ? "bg-red-600"
                    : row.status === "UPLOADED"
                    ? "bg-green-600"
                    : row.status === "READY"
                    ? "bg-yellow-600"
                    : "bg-blueText"
                }`}
                style={{ width: `${value}%` }}
              />
            </div>
          </div>
        );
      },
    },

    {
      header: "Server Response",
      accessor: "responseMessage",
      showTooltip: true,
      cell: (value: any, row: Record<string, any>) => {
        if (!value) return "-";
        return (
          <span
            className={`${
              row.status === "FAILED"
                ? "text-red-600"
                : row.status === "UPLOADED"
                ? "text-green-600"
                : "text-gray-600"
            }`}
          >
            {value}
          </span>
        );
      },
    },
    {
      header: "Status",
      accessor: "status",
      width: "150px",
      centerAlign: true,
      cell: (value: any) => (
        <span className={`status-badge ${value.toLowerCase()}`}>
          {value === "READY" ? "Ready to Upload" : value}
        </span>
      ),
    },
  ];

  const tableActions = [
    {
      label: "Delete",
      callback: (row: Record<string, any>) => {
        handleDelete((row as UploadState).id);
      },
      isDisabled: () => false,
    },
  ];

  return (
    <div className="p-[20px] job-history-upload-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Job History Upload</h1>
        <div className="flex space-x-4">
          <button className="secondary_button" onClick={handleDownloadTemplate}>
            Download Template
          </button>
          <button className="primary_button" onClick={handleUploadClick}>
            Upload File
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          id="fileInput"
          className="hidden"
          accept=".xlsx,.xls"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <h2 className="text-lg font-medium mb-2">Instructions</h2>
        <ul className="list-disc list-inside space-y-1 text-gray-600">
          <li>Download the template using the "Download Template" button</li>
          <li>Fill in the template with your job history data</li>
          <li>
            Upload one or more filled templates using the "Upload File" button
          </li>
          <li>Review and edit the data if needed before final submission</li>
          <li>Maximum file size: 10MB per file</li>
        </ul>
      </div>

      <StatsBoxContainer stats={statsData} />

      {uploads.length > 0 && (
        <div className="mt-8">
          <TableComponent
            columns={columns}
            data={uploads}
            showCheckboxes={false}
            showSerialNumbers={true}
            className="job-history-upload-table"
            actions={tableActions}
            showPagination={false}
            enableClientSideFiltering={false}
            isServerSidePagination={false}
          />
        </div>
      )}

      {isPreviewModalOpen && previewData && (
        <PreviewModal
          isOpen={isPreviewModalOpen}
          onClose={handlePreviewClose}
          data={previewData}
          isEditMode={previewData.isEditMode || false}
          onSubmit={handlePreviewSubmit}
        />
      )}
    </div>
  );
};

export default JobHistoryUploadComponent;
